// src/components/Rent/RentBeneficiaries/BeneficiariesFormSet.jsx

import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Button,
} from '@mui/material'
import {
    Add as AddIcon,
    DeleteOutline as DeleteOutlineIcon,
    InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material'

const emptyRow = {
    name: '',
    document: '',
    phone: '',
    email: '',
    percentage: '',
    pixKey: '',
}

const BeneficiariesFormSet = ({ value = [], onChange }) => {
    const [rows, setRows] = useState(value.length ? value : [emptyRow])

    useEffect(() => {
        if (value && value.length) {
            setRows(value)
        }
    }, [value])

    const handleFieldChange = (index, field, fieldValue) => {
        const newRows = [...rows]
        newRows[index] = {
            ...newRows[index],
            [field]: fieldValue,
        }
        setRows(newRows)
        onChange?.(newRows)
    }

    const handleAddRow = () => {
        const newRows = [...rows, emptyRow]
        setRows(newRows)
        onChange?.(newRows)
    }

    const handleRemoveRow = (index) => {
        const newRows = rows.filter((_, i) => i !== index)
        setRows(newRows.length ? newRows : [emptyRow])
        onChange?.(newRows.length ? newRows : [emptyRow])
    }

    const totalPercentage = useMemo(
        () =>
            rows.reduce((acc, row) => {
                const v = Number(String(row.percentage).replace(',', '.')) || 0
                return acc + v
            }, 0),
        [rows]
    )

    const distributionColor =
        totalPercentage === 100
            ? 'success'
            : totalPercentage > 100
                ? 'error'
                : 'warning'

    return (
        <Card>
            <CardHeader
                avatar={<InfoOutlinedIcon color="primary" />}
                title="Beneficiários"
                subheader='Para "SALVAR" é necessário que o valor de "Distribuição" chegue a 100% (soma das porcentagens)'
                action={
                    <Chip
                        label={`Distribuição: ${totalPercentage || 0}%`}
                        color={distributionColor}
                        variant={totalPercentage === 100 ? 'filled' : 'outlined'}
                    />
                }
                sx={{ '& .MuiCardHeader-subheader': { whiteSpace: 'normal' } }}
            />
            <CardContent sx={{ pt: 0 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>CPF / CNPJ</TableCell>
                                <TableCell>Telefone</TableCell>
                                <TableCell>E-mail</TableCell>
                                <TableCell align="right">% Distribuição</TableCell>
                                <TableCell>Chave Pix</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ minWidth: 160 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Nome completo"
                                            value={row.name}
                                            onChange={(e) =>
                                                handleFieldChange(index, 'name', e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 140 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="CPF ou CNPJ"
                                            value={row.document}
                                            onChange={(e) =>
                                                handleFieldChange(index, 'document', e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 130 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="(00) 00000-0000"
                                            value={row.phone}
                                            onChange={(e) =>
                                                handleFieldChange(index, 'phone', e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="email@exemplo.com"
                                            value={row.email}
                                            onChange={(e) =>
                                                handleFieldChange(index, 'email', e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 90 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            inputProps={{ min: 0, max: 100, step: 1 }}
                                            value={row.percentage}
                                            onChange={(e) =>
                                                handleFieldChange(index, 'percentage', e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 160 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Chave Pix"
                                            value={row.pixKey}
                                            onChange={(e) =>
                                                handleFieldChange(index, 'pixKey', e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ width: 60 }}>
                                        <Tooltip title="Remover beneficiário">
                      <span>
                        <IconButton
                            size="small"
                            onClick={() => handleRemoveRow(index)}
                            disabled={rows.length === 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box
                    mt={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                >
                    <Typography
                        variant="caption"
                        color={totalPercentage === 100 ? 'success.main' : 'error.main'}
                    >
                        {totalPercentage === 100
                            ? 'Distribuição OK: soma das porcentagens = 100%.'
                            : `A soma das porcentagens deve ser 100%. Atual: ${totalPercentage || 0}%.`}
                    </Typography>

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddRow}
                    >
                        Adicionar
                    </Button>
                </Box>
            </CardContent>
        </Card>
    )
}

export default BeneficiariesFormSet
